import React, { useState, useEffect, useCallback } from 'react';

const UnmergePopup = ({ onUnmergeConfirm }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [structureId, setStructureId] = useState(null);

  // Listen for unmerge popup requests
  useEffect(() => {
    const handleShowUnmergePopup = (event) => {
      const { structureId, position } = event.detail;
      
      setStructureId(structureId);
      setPopupPosition(position);
      setShowPopup(true);
    };

    window.addEventListener('showUnmergePopup', handleShowUnmergePopup);

    return () => {
      window.removeEventListener('showUnmergePopup', handleShowUnmergePopup);
    };
  }, []);

  const handleConfirm = useCallback(() => {
    if (structureId && onUnmergeConfirm) {
      onUnmergeConfirm(structureId);
    }
    setShowPopup(false);
    setStructureId(null);
  }, [structureId, onUnmergeConfirm]);

  const handleCancel = useCallback(() => {
    setShowPopup(false);
    setStructureId(null);
  }, []);

  // Handle outside click and keyboard events
  useEffect(() => {
    if (!showPopup) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-unmerge-popup]')) {
        handleCancel();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      } else if (event.key === 'Enter') {
        handleConfirm();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPopup, handleConfirm, handleCancel]);

  if (!showPopup) return null;

  const popupStyle = {
    position: 'fixed',
    left: `${popupPosition.x}px`,
    top: `${popupPosition.y}px`,
    background: 'linear-gradient(135deg, #404040 0%, #1a1a1a 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    minWidth: '200px'
  };

  const headerStyle = {
    marginBottom: '8px',
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const descriptionStyle = {
    marginBottom: '16px',
    fontSize: '13px',
    opacity: 0.9
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  };

  const buttonStyle = {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold'
  };

  const confirmButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  return (
    <div style={popupStyle} data-unmerge-popup>
      <div style={headerStyle}>
        <span>🔓</span>
        Unmerge Structure?
      </div>
      <div style={descriptionStyle}>
        This will separate the merged shapes back into individual components.
      </div>
      <div style={buttonContainerStyle}>
        <button 
          style={cancelButtonStyle}
          onClick={handleCancel}
          onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
        >
          Cancel
        </button>
        <button 
          style={confirmButtonStyle}
          onClick={handleConfirm}
          onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
        >
          Unmerge
        </button>
      </div>
    </div>
  );
};

export default UnmergePopup; 